import React, { useMemo } from "react";

import { Box, Grid } from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import { Theme, useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { camelCase, get } from "lodash";
import { makeStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import { Gender } from "@/gql/graphql";
import { colors } from "@/theme/overrides/colors";
import { format } from "@/utils/datetime";
import { percent, round } from "@/utils/number";
import { FragmentType } from "@/utils/type";
import { DeleteIconButton, EditIconButton } from "@components/IconButtons";

import AccordionDetailsItem from "./AccordionDetailsItem";
import AccordionSummaryItem from "./AccordionSummaryItem";
import { getUnit, removeUnit } from "../utils/getUnit";

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    width: "100%",
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(2),
  },
  accordion: {
    boxShadow: "none",
  },
  accordionSummary: {
    "&.MuiAccordionSummary-root.MuiButtonBase-root": {
      cursor: "default",
    },
  },
  accordionDetails: {
    backgroundColor: theme.palette.common.white,
    display: "flex",
    flexDirection: "column",
  },
  accordionDetailsField: {
    "&.MuiGrid-item": {
      paddingTop: 0,
      paddingBottom: 0,
    },
  },
  divider: {
    backgroundColor: "#D4DBE5",
    marginTop: 5,
    marginBottom: 5,
  },
  heading: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: "bold",
    whiteSpace: "nowrap",
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: "bold",
  },
  caption: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.text.secondary,
    fontWeight: 600,
  },
  image: {
    alignSelf: "center",
    color: colors.grey,
    objectFit: "contain",
    marginBottom: theme.spacing(2),
    height: 150,
    width: 150,
  },
  imageContainer: {
    backgroundColor: theme.palette.background.default,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    flexDirection: "column",
    marginRight: theme.spacing(2),
    padding: theme.spacing(2),
    textTransform: "capitalize",
  },
}));

type Props = {
  onEdit: () => void;
  onDelete: () => void;
  bodyMeasurements: FragmentType<typeof bodyMeasurementListItemFragment>;
  targetWeight: number;
  settings: FragmentType<typeof bodyMeasurementListItemSettingFragment>;
  gender: Gender | undefined;
  latestHeightCm: number | undefined;
  latestWeightKg: number | undefined;
};

const bodyMeasurementListItemFragment = graphql(`
  fragment BodyMeasurementListItem on BodyMeasurementsGrouped {
    date
    images {
      id
      imageUrl
      title
      bodyMeasurementId
      signedId
      image {
        id
        cloudinaryPreview
      }
    }
    abdominalGirthCm
    abdominalSkinfoldMm
    abdominalSubcutaneousFatThicknessMm
    ankleBreadthCm
    ankleGirthCm
    armFlexGirthCm
    armPerimeterCm
    armRelaxedGirthCm
    bicepsSkinfoldMm
    calfGirthCm
    chestGirthCm
    chestSkinfoldMm
    eightSkinfoldSum
    elbowBreadthMm
    femurBreadthMm
    fiveSkinfoldSum
    forearmGirthCm
    frontThighSkinfoldMm
    headCircumferenceCm
    heightCm
    hipCircumferenceCm
    iliacCrestSkinfoldMm
    medialCalfSkinfoldMm
    neckGirthCm
    shoulderGirthCm
    subscapularSkinfoldMm
    suprailiacSkinfoldMm
    supraspinalSkinfoldMm
    thighGirthCm
    tricepSkinfoldMm
    waistCircumferenceCm
    waistToHeightRatioPercentage
    waistToHipRatio
    waistToHipRatioPercentage
    weightKg
    wristBreadthCm
    wristGirthCm
    createdAt
  }
`);

const bodyMeasurementListItemSettingFragment = graphql(`
  fragment BodyMeasurementListItem_Setting on Setting {
    id
    bodyMeasurementFields {
      fieldName
      fieldLabel
      visible
    }
  }
`);

const BodyMeasurementListItem: React.FC<Props> = ({
  bodyMeasurements,
  onEdit,
  onDelete,
  settings,
}) => {
  const { classes } = useStyles();
  const theme = useTheme();
  const images = bodyMeasurements.images;

  const displayFields = useMemo(() => {
    if (settings == null || settings.bodyMeasurementFields == null) return null;

    return (
      <>
        {settings.bodyMeasurementFields.map((field) => {
          const key = camelCase(field.fieldName);
          const val = get(bodyMeasurements, key);

          if (field.fieldName === "weight_lbs") return null;

          const isVisible = val !== null || field.visible;
          if (!isVisible) return null;

          const unit = getUnit(field.fieldLabel);

          return (
            <Grid
              item
              key={field.fieldName}
              xs={12}
              md={6}
              lg={4}
              className={classes.accordionDetailsField}
            >
              <AccordionDetailsItem
                title={removeUnit(field.fieldLabel)}
                value={unit === "%" ? percent(val, { round: 0 }) : val}
                unit={unit}
              />
            </Grid>
          );
        })}
      </>
    );
  }, [settings, bodyMeasurements]);

  return (
    <Accordion className={classes.accordion} expanded>
      <AccordionSummary
        className={classes.accordionSummary}
        sx={{
          borderColor: "red",
          backgroundColor: colors.light,
          marginBottom: "8px",
        }}
      >
        <Box flex={1} display="flex" alignItems="center">
          <Box
            flex={1}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box
              display="flex"
              flexDirection="column"
              flexShrink={1}
              marginRight={3}
            >
              <Box maxWidth={150}>
                <Typography className={classes.heading}>
                  {format({ date: bodyMeasurements.date }, "d MMM, yyyy")}
                </Typography>
              </Box>
            </Box>
            <Box display="flex" alignItems="center">
              <AccordionSummaryItem
                title="Current Weight"
                text={`${bodyMeasurements?.weightKg ?? 0} kg`}
                rowStyle
                secondaryText={`(${round(
                  Number(bodyMeasurements?.weightKg) * 2.20462,
                  1
                ).toFixed(1)} lbs)`}
                icon="weight"
              />
              <Box
                marginLeft={4}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <EditIconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    onEdit();
                  }}
                />
                <Box marginX={2} />
                <DeleteIconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    onDelete();
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </AccordionSummary>

      <AccordionDetails className={classes.accordionDetails}>
        <Grid container spacing={4}>
          <Grid item container xs spacing={4}>
            {displayFields}
          </Grid>
        </Grid>
        <Box marginTop={theme.spacing(1)}>
          {images != null && (
            <>
              {images.length !== 0 && (
                <Typography className={classes.caption}>Body Images</Typography>
              )}
              <Box
                display="inline-flex"
                marginTop={theme.spacing(0.5)}
                flexWrap="wrap"
              >
                {images.map((image) => (
                  <Box
                    key={image.id}
                    className={classes.imageContainer}
                    marginTop={theme.spacing(0.5)}
                  >
                    <img
                      src={image.image?.cloudinaryPreview ?? ""}
                      className={classes.image}
                      alt=""
                    />
                    <Typography>{image.title}</Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default BodyMeasurementListItem;
